const BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const API_KEY = process.env.REACT_APP_API_KEY;

if (!API_KEY) {
  console.error(
    "REACT_APP_API_KEY is not set. " +
    "Create a .env file with REACT_APP_API_KEY=<your key>. " +
    "See .env.example for details."
  );
}

async function get(path, token) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (API_KEY) headers["X-API-Key"] = API_KEY;
  const res = await fetch(`${BASE}${path}`, { headers });
  if (!res.ok) {
    throw new Error(`API request failed: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || "API error");
  return json.data;
}

// ─── ENDPOINT MAP ─────────────────────────────────────────
// Maps frontend data key → backend endpoint path
// Used by the batch endpoint to resolve all data in ONE request
export const ENDPOINT_MAP = {
  // Users
  usersSummary:      "/users/summary",
  dauMau:            "/users/dau-mau",
  userGrowth:        "/users/growth",
  deviceSplit:       "/users/device-split",
  kycStatus:         "/users/kyc-status",
  suspensions:       "/users/suspensions",
  userActivity:      "/users/activity",
  referralFunnel:    "/users/referral-funnel",
  topPayingFans:     "/users/top-paying-fans",
  fansMaxCalls:      "/users/fans-max-calls",

  // Calls
  callsSummary:      "/calls/summary",
  callsSeries:       "/calls/series",
  callFailures:      "/calls/failure-breakdown",
  callQuality:       "/calls/quality",
  callQualityDevice: "/calls/quality-by-device",
  responseTime:      "/calls/response-time",
  callRatings:       "/calls/ratings",
  topCreators:       "/calls/top-creators",
  northStar:         "/calls/north-star",
  bookingPatterns:   "/calls/booking-patterns",

  // Finance
  gmv:               "/finance/gmv",
  gmvSeries:         "/finance/gmv/series",
  wallets:           "/finance/wallets",
  earnings:          "/finance/earnings",
  gstTrend:          "/finance/gst-trend",
  plSnapshot:        "/finance/pl-snapshot",
  pendingWithdrawals:"/finance/withdrawals/pending",
  withdrawalQueue:   "/finance/withdrawals/queue",
  paymentMethods:    "/finance/payment-methods",
  walletOffers:      "/finance/wallet-offers",
  businessMetrics:   "/finance/business-metrics",
  refundStats:       "/finance/refunds",
  couponStats:       "/finance/coupons",
  revenueTrend:      "/finance/revenue-trend",
  walletsByRole:        "/finance/wallets-by-role",
  completedWithdrawals: "/finance/withdrawals/completed",
  brandWalletTopups:    "/finance/brand-wallet-topups",
  fanTransactions:      "/finance/fan-transactions",

  // Brands
  brandsSummary:     "/brands/summary",
  brandRetention:    "/brands/retention",
  brandWallet:       "/brands/wallet",
  activeCampaigns:   "/brands/campaigns/active",
  campaignPipeline:  "/brands/campaigns/pipeline",
  campaignFulfill:   "/brands/campaigns/fulfillment",
  campaignEngage:    "/brands/campaigns/engagement",
  brandFinance:      "/brands/finance",

  // Creators
  creatorSupply:     "/creators/supply",
  creatorFunnel:     "/creators/onboarding-funnel",
  earningsDist:      "/creators/earnings/distribution",
  topEarners:        "/creators/earnings/top",
  atRiskCreators:    "/creators/at-risk",
  creatorAvail:      "/creators/availability",
  creatorFollowers:  "/creators/followers",
  instaHealth:       "/creators/instagram-health",
  commissionOverrides: "/creators/commission-overrides",
  creatorEarningsBySource: "/creators/earnings/by-source",

  // Support
  supportSummary:    "/support/summary",
  refundQueue:       "/support/refund-queue",
  searchAnalytics:   "/support/search-analytics",

  // Live
  liveSummary:       "/live/summary",
  liveEngagement:    "/live/engagement",

  // Notifications
  notifStats:        "/notifications/stats",
  broadcasts:        "/notifications/broadcasts",

  // Firebase / GA4
  firebaseStatus:    "/firebase/status",
  ga4ActiveUsers:    "/firebase/active-users",
  ga4UserGrowth:     "/firebase/user-growth",
  ga4Retention:      "/firebase/retention",
  ga4AppVersions:    "/firebase/app-versions",
  ga4DeviceSplit:    "/firebase/device-split",
  ga4Events:         "/firebase/events",
  ga4Screens:        "/firebase/screens",
  ga4Geo:            "/firebase/geo",
  ga4Acquisition:    "/firebase/acquisition",
  ga4Engagement:     "/firebase/engagement",
  ga4Realtime:       "/firebase/realtime",
  ga4Demographics:   "/firebase/demographics",
  ga4NewVsReturning: "/firebase/new-vs-returning",
  ga4PeakHours:      "/firebase/peak-hours",
  ga4DayOfWeek:      "/firebase/day-of-week",
  ga4SourceMedium:   "/firebase/source-medium",
  ga4DeviceModels:   "/firebase/device-models",
  ga4Languages:      "/firebase/languages",
  ga4PlatformSplit:  "/firebase/platform-split",
  ga4Countries:      "/firebase/countries",
  ga4InstallTrend:   "/firebase/install-uninstall",
  ga4Crashes:        "/firebase/crashes",
  firebaseAuthStats: "/firebase/auth-stats",
  appVersionByRole:  "/users/app-version-by-role",

  // Cashfree (payment gateway)
  cashfreeStatusApi:     "/cashfree/status",
  cashfreeSettleSummary: "/cashfree/settlements/summary",
  cashfreeSettleDaily:   "/cashfree/settlements/daily",
  cashfreeSettleRecent:  "/cashfree/settlements/recent",

  // Agora (live call status — only what current plan allows)
  agoraStatusApi:        "/agora/status",
  agoraLiveSummary:      "/agora/live/summary",
  agoraLiveChannels:     "/agora/live/channels",

  // Branch.io (install attribution)
  branchStatusApi:       "/branch/status",
  branchInstalls:        "/branch/installs/summary",
  branchInstallsDaily:   "/branch/installs/daily",
  branchFunnel:          "/branch/funnel",
  branchCampaigns:       "/branch/campaigns",

  // Meta Ads
  metaStatus:            "/meta/status",
  metaSummary:           "/meta/summary",
  metaSeries:            "/meta/series",
  metaCampaigns:         "/meta/campaigns",
  metaDemographics:      "/meta/demographics",
  metaPlatforms:         "/meta/platforms",
  metaActiveCampaigns:   "/meta/active-campaigns",

  // Mixpanel
  mixpanelStatus:        "/mixpanel/status",
  mpActiveUsers:         "/mixpanel/active-users",
  mpTopEvents:           "/mixpanel/top-events",
  mpEventTrend:          "/mixpanel/event-trend",
  mpEventProperties:     "/mixpanel/event-properties",
  mpFunnel:              "/mixpanel/funnel",
  mpRetention:           "/mixpanel/retention",
  mpSegmentation:        "/mixpanel/segmentation",
  mpEngagement:          "/mixpanel/engagement",
  mpRevenue:             "/mixpanel/revenue",
  mpFlows:               "/mixpanel/flows",
  mpStickiness:          "/mixpanel/stickiness",
  mpOnboardingFunnel:    "/mixpanel/onboarding-funnel",

  // Play Store
  playstoreSummary:      "/playstore/summary",
  playstoreInstalls:     "/playstore/installs",
  playstoreRatings:      "/playstore/ratings",
  playstoreCrashes:      "/playstore/crashes",

  // App Store
  appstoreSummary:       "/appstore/summary",
  appstoreDownloads:     "/appstore/downloads",
};

// Reverse map: endpoint path → frontend key
const REVERSE_MAP = {};
for (const [key, path] of Object.entries(ENDPOINT_MAP)) {
  REVERSE_MAP[path] = key;
}

/**
 * Batch API call — sends ONE POST request with all endpoint paths,
 * returns data keyed by frontend key names.
 *
 * @param {string[]} keys - Array of frontend keys (e.g. ["usersSummary", "callsSummary"])
 * @param {{ from: string, to: string }} range - Date range
 * @returns {Promise<Object>} - { usersSummary: data, callsSummary: data, ... }
 */
export async function batchFetch(keys, range, token) {
  const endpoints = keys.map(k => ENDPOINT_MAP[k]).filter(Boolean);

  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Always send API key as fallback — if JWT is expired, backend can still authenticate via API key
  if (API_KEY) headers["X-API-Key"] = API_KEY;

  // 35-second client-side timeout — backend allows 25s per endpoint for slow
  // external APIs (Firebase/Mixpanel/Meta), so keep client deadline above that.
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), 35000);

  let res;
  try {
    res = await fetch(`${BASE}/batch`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        endpoints,
        from: range?.from,
        to: range?.to,
      }),
      signal: ac.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    if (err.name === "AbortError") throw new Error("Batch API timed out after 20s");
    throw err;
  }
  clearTimeout(timer);

  if (res.status === 401 || res.status === 403) {
    // Auth lapsed — surface a specific error type so the UI can prompt re-login
    const e = new Error("AUTH_REQUIRED");
    e.code = "AUTH_REQUIRED";
    throw e;
  }
  if (!res.ok) {
    throw new Error(`Batch API request failed: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();
  if (!json.success) throw new Error("Batch API error");

  // Map results back to frontend keys — only include successful results.
  // Failed endpoints are skipped entirely so they don't overwrite cached
  // data with null (the LKG cache preserves the last good value).
  const data = {};
  for (const [path, result] of Object.entries(json.results)) {
    const key = REVERSE_MAP[path];
    if (key && result.ok) {
      data[key] = result.data;
    }
  }

  return { data, meta: json.meta };
}

// ─── AUTH HELPERS ─────────────────────────────────────────

/** Map HTTP status codes to user-friendly messages */
const FRIENDLY_ERRORS = {
  400: "Invalid request. Please check your input.",
  401: "Invalid credentials. Please check your username and password.",
  403: "You don't have permission to do that.",
  404: "Service not found. Please try again later.",
  429: "Too many attempts. Please wait a moment and try again.",
  500: "Server error. Please try again later.",
  502: "Server is temporarily unavailable. Please try again.",
  503: "Service is under maintenance. Please try again later.",
};

/** Handle auth fetch responses with user-friendly error messages */
async function authFetch(res, fallbackMsg = "Something went wrong. Please try again.") {
  if (!res.ok) {
    try {
      const errJson = await res.json();
      if (errJson.error?.message) throw new Error(errJson.error.message);
    } catch (e) {
      if (e.message && !e.message.includes("JSON") && !e.message.includes("Unexpected")) throw e;
    }
    throw new Error(FRIENDLY_ERRORS[res.status] || `${fallbackMsg} (Error ${res.status})`);
  }
  const json = await res.json();
  if (!json.success) throw new Error(json.error?.message || fallbackMsg);
  return json.data;
}

// ─── AUTH API ────────────────────────────────────────────
export const authApi = {
  login: async (username, password) => {
    const res = await fetch(`${BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    return authFetch(res, "Login failed. Please try again.");
  },

  me: async (token) => {
    const res = await fetch(`${BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Session expired. Please log in again.");
  },

  changePassword: async (token, current_password, new_password) => {
    const res = await fetch(`${BASE}/auth/change-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ current_password, new_password }),
    });
    return authFetch(res, "Password change failed. Please try again.");
  },

  requestAccess: async (token, role, reason) => {
    const res = await fetch(`${BASE}/auth/access-request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ role, reason }),
    });
    return authFetch(res, "Access request failed. Please try again.");
  },

  myRequests: async (token) => {
    const res = await fetch(`${BASE}/auth/my-requests`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Could not load requests.");
  },

  // Admin endpoints
  adminUsers: async (token) => {
    const res = await fetch(`${BASE}/auth/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Could not load users.");
  },

  adminPending: async (token) => {
    const res = await fetch(`${BASE}/auth/admin/pending`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Could not load pending requests.");
  },

  adminResolve: async (token, request_id, action) => {
    const res = await fetch(`${BASE}/auth/admin/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ request_id, action }),
    });
    return authFetch(res, "Could not resolve request.");
  },

  adminUpdateRoles: async (token, user_id, allowed_roles) => {
    const res = await fetch(`${BASE}/auth/admin/update-roles`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_id, allowed_roles }),
    });
    return authFetch(res, "Could not update roles.");
  },

  adminResetPassword: async (token, user_id, new_password) => {
    const res = await fetch(`${BASE}/auth/admin/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_id, new_password }),
    });
    return authFetch(res, "Could not reset password.");
  },

  adminDeleteUser: async (token, user_id) => {
    const res = await fetch(`${BASE}/auth/admin/users/${user_id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Could not delete user.");
  },

  adminToggleUser: async (token, user_id, is_active) => {
    const res = await fetch(`${BASE}/auth/admin/toggle-user`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_id, is_active }),
    });
    return authFetch(res, "Could not update user status.");
  },

  // Restricted Accounts (test account exclusion)
  getRestrictedAccounts: async (token) => {
    const res = await fetch(`${BASE}/auth/admin/restricted`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Could not load restricted accounts.");
  },

  addRestrictedAccount: async (token, account_id, account_type, name, username, reason) => {
    const res = await fetch(`${BASE}/auth/admin/restricted`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ account_id, account_type, name, username, reason }),
    });
    return authFetch(res, "Could not add restricted account.");
  },

  removeRestrictedAccount: async (token, id) => {
    const res = await fetch(`${BASE}/auth/admin/restricted/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Could not remove restricted account.");
  },

  searchAccounts: async (token, query, type) => {
    const params = new URLSearchParams();
    if (query) params.append("q", query);
    if (type && type !== "all") params.append("type", type);
    const res = await fetch(`${BASE}/auth/admin/search-accounts?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return authFetch(res, "Search failed.");
  },

  // AI Assistant
  askAI: async (token, question) => {
    // Send BOTH the Bearer JWT (issued at login) AND the X-API-Key (env var)
    // so the route accepts whichever auth path is configured. Backend's
    // authenticate() middleware checks X-API-Key first, then JWT — either
    // succeeding allows the request through.
    const apiKey = process.env.REACT_APP_API_KEY;
    const headers = { "Content-Type": "application/json" };
    if (apiKey) headers["X-API-Key"] = apiKey;
    if (token)  headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${BASE}/ai/ask`, {
      method: "POST",
      headers,
      body: JSON.stringify({ question }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.success) {
      // Backend error envelope is `{ error: { code, message } }` (operational
      // errors) OR a plain string (legacy). Extract the message robustly so
      // the UI never shows "[object Object]".
      const err = json.error;
      let msg;
      if (!err) msg = `Request failed (HTTP ${res.status})`;
      else if (typeof err === "string") msg = err;
      else if (typeof err === "object" && err.message) msg = err.message;
      else msg = JSON.stringify(err);
      throw new Error(msg);
    }
    return json.data;
  },
};

// ─── LEGACY INDIVIDUAL API CALLS ──────────────────────────
// Kept for fallback / individual refreshes if needed
const rangeQS = (r) => `from=${r.from}&to=${r.to}`;

export const api = {
  health: () => get("/health"),
  usersSummary:      () => get("/users/summary"),
  dauMau:            () => get("/users/dau-mau"),
  userGrowth:        (range, granularity = "day") => get(`/users/growth?${rangeQS(range)}&granularity=${granularity}`),
  deviceSplit:       (range) => get(`/users/device-split?${rangeQS(range)}`),
  kycStatus:         () => get("/users/kyc-status"),
  suspensions:       (range) => get(`/users/suspensions?${rangeQS(range)}`),
  userActivity:      (range) => get(`/users/activity?${rangeQS(range)}`),
  referralFunnel:    (range) => get(`/users/referral-funnel?${rangeQS(range)}`),
  topPayingFans:     (range, limit = 20) => get(`/users/top-paying-fans?${rangeQS(range)}&limit=${limit}`),
  fansMaxCalls:      (range, limit = 20) => get(`/users/fans-max-calls?${rangeQS(range)}&limit=${limit}`),
  callsSummary:      () => get("/calls/summary"),
  callsSeries:       (range, granularity = "day") => get(`/calls/series?${rangeQS(range)}&granularity=${granularity}`),
  callFailures:      (range) => get(`/calls/failure-breakdown?${rangeQS(range)}`),
  callQuality:       (range) => get(`/calls/quality?${rangeQS(range)}`),
  callQualityDevice: (range) => get(`/calls/quality-by-device?${rangeQS(range)}`),
  responseTime:      (range) => get(`/calls/response-time?${rangeQS(range)}`),
  callRatings:       (range) => get(`/calls/ratings?${rangeQS(range)}`),
  topCreators:       (range, limit = 20) => get(`/calls/top-creators?${rangeQS(range)}&limit=${limit}`),
  northStar:         (range) => get(`/calls/north-star?${rangeQS(range)}`),
  bookingPatterns:   (range) => get(`/calls/booking-patterns?${rangeQS(range)}`),
  gmv:               (range) => get(`/finance/gmv?${rangeQS(range)}`),
  gmvSeries:         (range, granularity = "day") => get(`/finance/gmv/series?${rangeQS(range)}&granularity=${granularity}`),
  wallets:           () => get("/finance/wallets"),
  earnings:          (range) => get(`/finance/earnings?${rangeQS(range)}`),
  gstTrend:          () => get("/finance/gst-trend"),
  plSnapshot:        (range) => get(`/finance/pl-snapshot?${rangeQS(range)}`),
  pendingWithdrawals:() => get("/finance/withdrawals/pending"),
  withdrawalQueue:   () => get("/finance/withdrawals/queue"),
  paymentMethods:    (range) => get(`/finance/payment-methods?${rangeQS(range)}`),
  walletOffers:      (range) => get(`/finance/wallet-offers?${rangeQS(range)}`),
  businessMetrics:   (range) => get(`/finance/business-metrics?${rangeQS(range)}`),
  refundStats:       (range) => get(`/finance/refunds?${rangeQS(range)}`),
  couponStats:       (range) => get(`/finance/coupons?${rangeQS(range)}`),
  revenueTrend:      () => get("/finance/revenue-trend"),
  brandsSummary:     () => get("/brands/summary"),
  brandsList:        (page = 1) => get(`/brands/list?page=${page}&limit=20`),
  brandRetention:    () => get("/brands/retention"),
  brandWallet:       () => get("/brands/wallet"),
  campaignPipeline:  (range) => get(`/brands/campaigns/pipeline?${rangeQS(range)}`),
  activeCampaigns:   () => get("/brands/campaigns/active"),
  campaignFulfill:   (range) => get(`/brands/campaigns/fulfillment?${rangeQS(range)}`),
  campaignEngage:    (range) => get(`/brands/campaigns/engagement?${rangeQS(range)}`),
  brandFinance:      (range) => get(`/brands/finance?${rangeQS(range)}`),
  creatorSupply:     () => get("/creators/supply"),
  creatorFunnel:     () => get("/creators/onboarding-funnel"),
  earningsDist:      (range) => get(`/creators/earnings/distribution?${rangeQS(range)}`),
  topEarners:        (range, limit = 20) => get(`/creators/earnings/top?${rangeQS(range)}&limit=${limit}`),
  atRiskCreators:    () => get("/creators/at-risk"),
  creatorAvail:      () => get("/creators/availability"),
  creatorFollowers:  () => get("/creators/followers"),
  instaHealth:       () => get("/creators/instagram-health"),
  commissionOverrides: () => get("/creators/commission-overrides"),
  creatorEarningsBySource: (range, limit = 20) => get(`/creators/earnings/by-source?${rangeQS(range)}&limit=${limit}`),
  supportSummary:    () => get("/support/summary"),
  refundQueue:       () => get("/support/refund-queue"),
  searchAnalytics:   (range) => get(`/support/search-analytics?${rangeQS(range)}`),
  liveSummary:       (range) => get(`/live/summary?${rangeQS(range)}`),
  liveEngagement:    (range) => get(`/live/engagement?${rangeQS(range)}`),
  notifStats:        (range) => get(`/notifications/stats?${rangeQS(range)}`),
  broadcasts:        (range) => get(`/notifications/broadcasts?${rangeQS(range)}`),
  firebaseStatus:    () => get("/firebase/status"),
  ga4ActiveUsers:    () => get("/firebase/active-users"),
  ga4UserGrowth:     (range) => get(`/firebase/user-growth?${rangeQS(range)}`),
  ga4Retention:      () => get("/firebase/retention"),
  ga4AppVersions:    () => get("/firebase/app-versions"),
  ga4DeviceSplit:    (range) => get(`/firebase/device-split?${rangeQS(range)}`),
  ga4Events:         (range) => get(`/firebase/events?${rangeQS(range)}`),
  ga4Screens:        (range) => get(`/firebase/screens?${rangeQS(range)}`),
  ga4Geo:            (range) => get(`/firebase/geo?${rangeQS(range)}`),
  ga4Acquisition:    (range) => get(`/firebase/acquisition?${rangeQS(range)}`),
  ga4Engagement:     (range) => get(`/firebase/engagement?${rangeQS(range)}`),
  ga4Realtime:       () => get("/firebase/realtime"),
  ga4Demographics:   (range) => get(`/firebase/demographics?${rangeQS(range)}`),
  ga4NewVsReturning: (range) => get(`/firebase/new-vs-returning?${rangeQS(range)}`),
  ga4PeakHours:      (range) => get(`/firebase/peak-hours?${rangeQS(range)}`),
  ga4DayOfWeek:      (range) => get(`/firebase/day-of-week?${rangeQS(range)}`),
  ga4SourceMedium:   (range) => get(`/firebase/source-medium?${rangeQS(range)}`),
  ga4DeviceModels:   () => get("/firebase/device-models"),
  ga4Languages:      (range) => get(`/firebase/languages?${rangeQS(range)}`),
  ga4PlatformSplit:  (range) => get(`/firebase/platform-split?${rangeQS(range)}`),
  ga4Countries:      (range) => get(`/firebase/countries?${rangeQS(range)}`),
  ga4InstallTrend:   (range) => get(`/firebase/install-uninstall?${rangeQS(range)}`),
  ga4Crashes:        (range) => get(`/firebase/crashes?${rangeQS(range)}`),
  firebaseAuthStats: () => get("/firebase/auth-stats"),
  // Mixpanel
  mixpanelStatus:      () => get("/mixpanel/status"),
  mpActiveUsers:       () => get("/mixpanel/active-users"),
  mpTopEvents:         (range) => get(`/mixpanel/top-events?${rangeQS(range)}`),
  mpEventTrend:        (range) => get(`/mixpanel/event-trend?${rangeQS(range)}`),
  mpEventProperties:   (range) => get(`/mixpanel/event-properties?${rangeQS(range)}`),
  mpFunnel:            (range) => get(`/mixpanel/funnel?${rangeQS(range)}`),
  mpRetention:         (range) => get(`/mixpanel/retention?${rangeQS(range)}`),
  mpSegmentation:      (range) => get(`/mixpanel/segmentation?${rangeQS(range)}`),
  mpEngagement:        (range) => get(`/mixpanel/engagement?${rangeQS(range)}`),
  mpRevenue:           (range) => get(`/mixpanel/revenue?${rangeQS(range)}`),
  mpFlows:             (range) => get(`/mixpanel/flows?${rangeQS(range)}`),
  mpStickiness:        (range) => get(`/mixpanel/stickiness?${rangeQS(range)}`),
  mpOnboardingFunnel:  (range) => get(`/mixpanel/onboarding-funnel?${rangeQS(range)}`),
};
